// ─── Self-heal a stale lazy chunk after a deploy ─────────────────────────────
//
// Every page (and a few dialogs) is code-split, so the running app holds
// references to hashed chunk filenames like `BacklogItemDetail-A1b2c3.js`. A
// deploy rotates those hashes, so a long-open tab / installed PWA that then
// lazy-loads a not-yet-fetched chunk requests a filename the server no longer
// has → `import()` rejects with "Failed to fetch dynamically imported module"
// and the rejection bubbles past <Suspense> to the top-level ErrorBoundary.
//
// The cure is a single full reload: a deep-link navigation (e.g. /backlog/:id)
// bypasses the service worker (sw.ts registers no navigateFallback), so the
// reload fetches the fresh index.html with the new chunk names. (The bare `/`
// route IS precached, so a reload there may re-boot the old index until the new
// SW activates — but that path is already covered by ReloadPrompt's
// controllerchange/update-poll reload, and the heal stays loop-safe regardless.)
// `importChunk` wraps a dynamic import so the heal happens automatically, at
// most a few times per session, behind a rate + lifetime loop guard.

import { isStaleClientBuild } from '@pcp/shared';
import { APP_VERSION } from './version';

/**
 * True when a rejected `import()` looks like the chunk URL was unreachable — a
 * stale hashed filename after a deploy, the Vite preload helper failing, a MIME
 * mismatch (SPA fallback served index.html for a missing .js), or the network
 * being down. Browsers word this very differently, so match all known variants.
 */
export function isStaleChunkError(err: unknown): boolean {
  const e = err as { message?: string; name?: string } | null | undefined;
  const text = `${e?.name ?? ''} ${e?.message ?? (typeof err === 'string' ? err : '')}`;
  return (
    /failed to fetch dynamically imported module/i.test(text) ||
    /error loading dynamically imported module/i.test(text) ||
    /importing a module script failed/i.test(text) || // Safari
    /module script failed/i.test(text) ||
    /failed to load module script/i.test(text) || // Chrome MIME mismatch
    /is not a valid javascript mime type/i.test(text) || // MIME mismatch
    /unable to preload (?:css|module)/i.test(text) || // Vite __vitePreload helper
    /loading chunk [\d]+ failed/i.test(text) || // bundler-agnostic, defensive
    /chunkloaderror/i.test(text)
  );
}

// Two independent loop guards, both keyed in sessionStorage (per tab session):
//   • RATE: at most one reload per RELOAD_DEBOUNCE_MS, so a fast re-import can't
//     reload-spin. The timestamp persists across the reload so the freshly-booted
//     page can tell it JUST reloaded.
//   • LIFETIME: at most MAX_RELOADS forced reloads, so a genuinely broken /
//     persistently-stale deploy gives up to the ErrorBoundary instead of
//     reloading forever (covers the case where boot takes longer than the rate
//     window). The counter resets on the first clean chunk load — a healthy
//     session that heals once still gets a fresh budget for a LATER real deploy.
const RELOAD_TS_KEY = 'pcp:chunk-reload-ts';
const RELOAD_COUNT_KEY = 'pcp:chunk-reload-count';
const RELOAD_DEBOUNCE_MS = 20_000;
const MAX_RELOADS = 3;

/**
 * Returns true (and records the attempt) only if BOTH guards allow it: we have
 * not reloaded in the last RELOAD_DEBOUNCE_MS, and we are under MAX_RELOADS for
 * this session. If sessionStorage is unavailable we cannot dedupe across reloads,
 * so we refuse the auto-reload (the ErrorBoundary still offers a manual one)
 * rather than risk a reload loop.
 */
function claimReload(): boolean {
  try {
    if (Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || 0) >= MAX_RELOADS) return false;
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0);
    if (Date.now() - last < RELOAD_DEBOUNCE_MS) return false;
    sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
    sessionStorage.setItem(
      RELOAD_COUNT_KEY,
      String(Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || 0) + 1),
    );
    return true;
  } catch {
    return false;
  }
}

/** A chunk loaded cleanly → the deployed build is healthy, so reset the lifetime
 *  give-up counter; a LATER deploy that goes stale gets a fresh reload budget. */
function noteChunkSuccess(): void {
  try {
    if (sessionStorage.getItem(RELOAD_COUNT_KEY)) sessionStorage.removeItem(RELOAD_COUNT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Kick off one loop-guarded heal-reload to pick up the freshly-deployed build.
 * Returns true if a reload was actually started (the caller should stop — the
 * page is unloading), or false if a guard refused / we're offline / can't dedupe
 * (the caller should fall back to its normal error handling, no loop).
 */
export function maybeReloadForStaleChunk(): boolean {
  if (typeof window === 'undefined') return false;
  // Offline: a reload would just drop the running PWA shell onto the browser's
  // offline page for what is a transient connectivity blip, not a stale deploy.
  // Hold the shell and let Suspense/the boundary surface it instead.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (!claimReload()) return false;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn('[chunkReload] stale app chunk — reloading once to pick up the new build');
  }
  window.location.reload();
  return true;
}

/**
 * Wrap a dynamic `import()` so a stale-chunk failure self-heals. Drop-in around
 * any `() => import('./X')` loader; preserves the resolved module type.
 *
 *   const Page = lazy(() => importChunk(() => import('./pages/Page')));
 *
 * Behaviour: on a stale/network failure, retry once after a short backoff (a
 * brief debounce before committing to a reload; on engines that re-fetch rather
 * than cache the module-map failure it can also rescue a sub-second blip); if it
 * still fails as a stale chunk, force one loop-guarded reload. A real module-eval
 * error is rethrown untouched so the boundary shows it.
 */
export function importChunk<T>(loader: () => Promise<T>): Promise<T> {
  const onLoaded = (m: T): T => { noteChunkSuccess(); return m; };
  return loader().then(onLoaded, (err) => {
    if (!isStaleChunkError(err)) throw err; // genuine module error — surface it
    return new Promise<T>((resolve) => setTimeout(resolve, 400))
      .then(() => loader())
      .then(onLoaded)
      .catch((err2) => {
        if (isStaleChunkError(err2) && maybeReloadForStaleChunk()) {
          // Reload in flight — park on a never-settling promise so the caller
          // keeps showing its Suspense fallback (no error flash, no spurious
          // boundary report) until the page actually unloads.
          return new Promise<T>(() => {});
        }
        throw err2; // already reloaded & still broken, or can't dedupe — surface it
      });
  });
}

// ─── Self-heal a crash caused by a stale BUILD (not just a stale chunk) ──────
//
// The heal above covers a stale lazy chunk, which announces itself with a
// recognisable import failure. The same deploy skew has a second, quieter form:
// last release's bundle runs fine until the new API answers something it cannot
// read, and it crashes on ordinary render code with an ordinary TypeError. That
// is what b-01795 did — six error-boundary crashes across two people, every one
// of them curable by a single reload the user had to think to press.
//
// The message can't classify that error, so ask the server instead: if the build
// we are RUNNING is not the build that is DEPLOYED, the crash is skew and a
// reload is the fix. If the versions match, the crash is a real bug in live code
// — surface it, never reload, or a deterministic crash would loop.
//
// Shares the loop guards above on purpose: one budget across both heals, so the
// two paths can't reload twice between them.

/**
 * The release tag the server is actually running, from GET /api/health. Null
 * when it can't be established — offline, mid-deploy (the API is restarting),
 * or an API too old to report it — and null always means "don't reload".
 *
 * Read regardless of HTTP status: /api/health answers 503 when a dependency is
 * degraded, and the version it reports is still the truth.
 */
export async function deployedBuildVersion(): Promise<string | null> {
  try {
    const res = await fetch('/api/health', { cache: 'no-store' });
    const body = await res.json().catch(() => null);
    const v = (body as { deployment?: unknown } | null)?.deployment;
    return typeof v === 'string' && v ? v : null;
  } catch {
    return null;
  }
}

/**
 * Reload once — and only once — if this crash is deploy skew rather than a bug.
 *
 * Resolves true when a reload is in flight (the caller's UI is about to be
 * replaced anyway), false when the running build IS current, the check failed,
 * or a loop guard refused. Never throws: a heal that cannot run must leave the
 * boundary's normal fallback exactly as it was.
 */
export async function maybeReloadForStaleBuild(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  // A local/unversioned build can never be "stale" — settle it before spending a
  // request, so `pnpm dev` crashes stay on screen with no extra network noise.
  if (APP_VERSION === 'dev') return false;
  const deployed = await deployedBuildVersion();
  if (!isStaleClientBuild(APP_VERSION, deployed)) return false;
  if (!claimReload()) return false;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[chunkReload] crash on stale build ${APP_VERSION} (deployed ${deployed}) — reloading once`);
  }
  window.location.reload();
  return true;
}
